import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiActivity,
    FiArrowLeft,
    FiBarChart2,
    FiCalendar,
    FiCheckCircle,
    FiDollarSign,
    FiEdit,
    FiMail,
    FiMapPin,
    FiMessageSquare,
    FiPhone,
    FiTrendingUp,
    FiTruck,
    FiUser
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../services/api';

const getImageSrc = (image) => {
  if (!image) return '';
  return typeof image === 'string' ? image : (image.url || image.uri || image.secure_url || image.path || '');
};

const SellerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [cars, setCars] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, cars, enquiries, activity

  useEffect(() => {
    fetchSellerDetails();
  }, [id]);

  const fetchSellerDetails = async () => {
    try {
      setLoading(true);

      // Fetch seller details
      const sellerResponse = await ApiService.get(`/sellers/${id}`);
      if (sellerResponse.success) {
        setSeller(sellerResponse.data.seller);
      }

      // Fetch seller's cars
      try {
        const carsResponse = await ApiService.get(`/cars/seller/${id}`);
        if (carsResponse.success) {
          setCars(carsResponse.data.cars || []);
        }
      } catch (err) {
        console.log('No cars found or error fetching cars');
        setCars([]);
      }

      // Fetch seller's enquiries
      try {
        const enquiriesResponse = await ApiService.get(`/enquiries?sellerId=${id}`);
        if (enquiriesResponse.success) {
          setEnquiries(enquiriesResponse.data.enquiries || []);
        }
      } catch (err) {
        console.log('No enquiries found or error fetching enquiries');
        setEnquiries([]);
      }

    } catch (error) {
      console.error('Error fetching seller details:', error);
      toast.error('Failed to load seller details');
      navigate('/dashboard/sellers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          <p className="mt-4 text-gray-600">Loading seller details...</p>
        </div>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Seller not found</p>
          <button
            onClick={() => navigate('/dashboard/sellers')}
            className="mt-4 text-orange-600 hover:text-orange-700"
          >
            Back to Sellers
          </button>
        </div>
      </div>
    );
  }

  const stats = seller.statistics || {};
  const soldCars = cars.filter(car => car.status === 'sold').length;
  const activeCars = cars.filter(car => car.status === 'active').length;
  const blockedCars = cars.filter(car => car.status === 'blocked').length;
  const totalRevenue = cars
    .filter(car => car.status === 'sold')
    .reduce((sum, car) => sum + (car.soldPrice || car.price || 0), 0);
  
  const attendedEnquiries = enquiries.filter(e => e.status === 'responded' || e.status === 'closed').length;
  const pendingEnquiries = enquiries.filter(e => e.status === 'pending' || e.status === 'new').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard/sellers')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <FiArrowLeft className="h-6 w-6 text-gray-600" />
              </motion.button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {seller.firstName} {seller.lastName}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {seller.businessName || 'Individual Seller'}
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/dashboard/sellers/add?edit=${id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              <FiEdit className="h-4 w-4" />
              Edit Seller
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            icon={FiTruck}
            label="Total Cars Added"
            value={cars.length}
            color="blue"
          />
          <StatsCard
            icon={FiDollarSign}
            label="Cars Sold"
            value={soldCars}
            subtext={`₹${(totalRevenue / 100000).toFixed(2)}L revenue`}
            color="green"
          />
          <StatsCard
            icon={FiMessageSquare}
            label="Enquiries Attended"
            value={attendedEnquiries}
            subtext={`${pendingEnquiries} pending`}
            color="purple"
          />
          <StatsCard
            icon={FiActivity}
            label="Active Listings"
            value={activeCars}
            subtext={`${blockedCars} blocked`}
            color="orange"
          />
        </div>

        {/* Profile and Details Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiUser className="text-orange-600" />
              Profile Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <FiMail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{seller.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiPhone className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{seller.phone || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiMapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="text-sm font-medium text-gray-900">
                    {seller.address?.city || 'N/A'}, {seller.address?.state || ''}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FiCalendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Joined</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(seller.createdAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Status Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiCheckCircle className="text-orange-600" />
              Account Status
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Account Status</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  seller.isActive 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {seller.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Verification</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  seller.isVerified 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {seller.isVerified ? 'Verified' : 'Pending'}
                </span>
              </div>
              {seller.businessName && (
                <div className="pt-4 border-t">
                  <p className="text-xs text-gray-500 mb-1">Business Name</p>
                  <p className="text-sm font-medium text-gray-900">{seller.businessName}</p>
                </div>
              )}
              {seller.gstNumber && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">GST Number</p>
                  <p className="text-sm font-medium text-gray-900">{seller.gstNumber}</p>
                </div>
              )}
              {(seller.documents?.panCard || seller.documents?.aadharCard || seller.documents?.drivingLicense) && (
                <div className="pt-4 border-t">
                  <p className="text-xs text-gray-500 mb-2">Document Details</p>
                  {seller.documents?.panCard && (
                    <p className="text-sm text-gray-900 mb-1"><span className="font-medium">PAN:</span> {seller.documents.panCard}</p>
                  )}
                  {seller.documents?.aadharCard && (
                    <p className="text-sm text-gray-900 mb-1"><span className="font-medium">Aadhaar:</span> {seller.documents.aadharCard}</p>
                  )}
                  {seller.documents?.drivingLicense && (
                    <p className="text-sm text-gray-900"><span className="font-medium">Driving License:</span> {seller.documents.drivingLicense}</p>
                  )}
                </div>
              )}
              {(seller.bankDetails?.accountHolderName || seller.bankDetails?.accountNumber || seller.bankDetails?.ifscCode) && (
                <div className="pt-4 border-t">
                  <p className="text-xs text-gray-500 mb-2">Bank Details</p>
                  {seller.bankDetails?.accountHolderName && (
                    <p className="text-sm text-gray-900 mb-1"><span className="font-medium">Account Holder:</span> {seller.bankDetails.accountHolderName}</p>
                  )}
                  {seller.bankDetails?.accountNumber && (
                    <p className="text-sm text-gray-900 mb-1"><span className="font-medium">Account Number:</span> {seller.bankDetails.accountNumber}</p>
                  )}
                  {seller.bankDetails?.ifscCode && (
                    <p className="text-sm text-gray-900"><span className="font-medium">IFSC:</span> {seller.bankDetails.ifscCode}</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Performance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiBarChart2 className="text-orange-600" />
              Performance
            </h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Conversion Rate</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {cars.length > 0 ? ((soldCars / cars.length) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${cars.length > 0 ? (soldCars / cars.length) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Response Rate</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {enquiries.length > 0 ? ((attendedEnquiries / enquiries.length) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ width: `${enquiries.length > 0 ? (attendedEnquiries / enquiries.length) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{(totalRevenue / 100000).toFixed(2)}L
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Avg: ₹{soldCars > 0 ? ((totalRevenue / soldCars) / 100000).toFixed(2) : 0}L per sale
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b">
            <nav className="flex -mb-px">
              <TabButton
                active={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
                icon={FiActivity}
                label="Overview"
              />
              <TabButton
                active={activeTab === 'cars'}
                onClick={() => setActiveTab('cars')}
                icon={FiTruck}
                label={`Cars (${cars.length})`}
              />
              <TabButton
                active={activeTab === 'enquiries'}
                onClick={() => setActiveTab('enquiries')}
                icon={FiMessageSquare}
                label={`Enquiries (${enquiries.length})`}
              />
              <TabButton
                active={activeTab === 'activity'}
                onClick={() => setActiveTab('activity')}
                icon={FiTrendingUp}
                label="Activity"
              />
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && <OverviewTab seller={seller} cars={cars} enquiries={enquiries} />}
            {activeTab === 'cars' && <CarsTab cars={cars} />}
            {activeTab === 'enquiries' && <EnquiriesTab enquiries={enquiries} />}
            {activeTab === 'activity' && <ActivityTab seller={seller} cars={cars} enquiries={enquiries} />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subtext, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="bg-white rounded-xl shadow-sm p-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {subtext && (
            <p className="text-xs text-gray-500 mt-1">{subtext}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </motion.div>
  );
};

// Tab Button Component
const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-orange-600 text-orange-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

// Overview Tab
const OverviewTab = ({ seller, cars, enquiries }) => {
  const recentCars = cars.slice(0, 5);
  const recentEnquiries = enquiries.slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Recent Cars Added</h3>
        {recentCars.length === 0 ? (
          <p className="text-gray-500 text-sm">No cars added yet</p>
        ) : (
          <div className="space-y-3">
            {recentCars.map(car => (
              <div key={car._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {getImageSrc(car.images?.[0]) ? (
                    <img 
                      src={getImageSrc(car.images?.[0])} 
                      alt={car.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400">
                      <FiTruck className="h-5 w-5" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{car.title}</p>
                    <p className="text-sm text-gray-500">
                      {car.year} • {car.fuelType}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">₹{(car.price / 100000).toFixed(2)}L</p>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                    car.status === 'sold' ? 'bg-green-100 text-green-700' :
                    car.status === 'active' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {car.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Recent Enquiries</h3>
        {recentEnquiries.length === 0 ? (
          <p className="text-gray-500 text-sm">No enquiries yet</p>
        ) : (
          <div className="space-y-3">
            {recentEnquiries.map(enquiry => (
              <div key={enquiry._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{enquiry.customerName || 'Anonymous'}</p>
                  <p className="text-sm text-gray-500">{enquiry.message?.substring(0, 50)}...</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    {new Date(enquiry.createdAt).toLocaleDateString()}
                  </p>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                    enquiry.status === 'responded' || enquiry.status === 'closed' 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {enquiry.status || 'pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Cars Tab
const CarsTab = ({ cars }) => {
  const [filter, setFilter] = useState('all'); // all, active, sold, blocked

  const filteredCars = filter === 'all' 
    ? cars 
    : cars.filter(car => car.status === filter);

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={cars.length} />
        <FilterButton active={filter === 'active'} onClick={() => setFilter('active')} label="Active" count={cars.filter(c => c.status === 'active').length} />
        <FilterButton active={filter === 'sold'} onClick={() => setFilter('sold')} label="Sold" count={cars.filter(c => c.status === 'sold').length} />
        <FilterButton active={filter === 'blocked'} onClick={() => setFilter('blocked')} label="Blocked" count={cars.filter(c => c.status === 'blocked').length} />
      </div>

      {filteredCars.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No cars found</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCars.map(car => (
            <motion.div
              key={car._id}
              whileHover={{ y: -4 }}
              className="bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {getImageSrc(car.images?.[0]) ? (
                <img 
                  src={getImageSrc(car.images?.[0])} 
                  alt={car.title}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400">
                  <FiTruck className="h-8 w-8" />
                </div>
              )}
              <div className="p-4">
                <h4 className="font-semibold text-gray-900 mb-2">{car.title}</h4>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">{car.year} • {car.fuelType}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    car.status === 'sold' ? 'bg-green-100 text-green-700' :
                    car.status === 'active' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {car.status}
                  </span>
                </div>
                <p className="text-lg font-bold text-gray-900">₹{(car.price / 100000).toFixed(2)}L</p>
                {car.status === 'sold' && car.soldPrice && (
                  <p className="text-sm text-green-600">Sold for: ₹{(car.soldPrice / 100000).toFixed(2)}L</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Enquiries Tab
const EnquiriesTab = ({ enquiries }) => {
  const [filter, setFilter] = useState('all');

  const filteredEnquiries = filter === 'all'
    ? enquiries
    : enquiries.filter(e => e.status === filter);

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={enquiries.length} />
        <FilterButton active={filter === 'pending'} onClick={() => setFilter('pending')} label="Pending" count={enquiries.filter(e => e.status === 'pending' || e.status === 'new').length} />
        <FilterButton active={filter === 'responded'} onClick={() => setFilter('responded')} label="Responded" count={enquiries.filter(e => e.status === 'responded').length} />
        <FilterButton active={filter === 'closed'} onClick={() => setFilter('closed')} label="Closed" count={enquiries.filter(e => e.status === 'closed').length} />
      </div>

      {filteredEnquiries.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No enquiries found</p>
      ) : (
        <div className="space-y-4">
          {filteredEnquiries.map(enquiry => (
            <motion.div
              key={enquiry._id}
              whileHover={{ x: 4 }}
              className="bg-gray-50 rounded-lg p-4 border border-gray-200"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-900">{enquiry.customerName || 'Anonymous Customer'}</p>
                  <p className="text-sm text-gray-500">{enquiry.customerEmail || enquiry.customerPhone}</p>
                </div>
                <span className={`px-3 py-1 text-xs rounded-full ${
                  enquiry.status === 'responded' || enquiry.status === 'closed'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {enquiry.status || 'pending'}
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-2">{enquiry.message}</p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Car: {enquiry.carTitle || 'N/A'}</span>
                <span>{new Date(enquiry.createdAt).toLocaleDateString('en-IN')}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Activity Tab
const ActivityTab = ({ seller, cars, enquiries }) => {
  // Combine cars and enquiries into activity timeline
  const activities = [
    ...cars.map(car => ({
      type: 'car',
      date: car.createdAt,
      title: car.status === 'sold' ? `Sold ${car.title}` : `Added ${car.title}`,
      icon: car.status === 'sold' ? FiDollarSign : FiTruck,
      color: car.status === 'sold' ? 'green' : 'blue',
      details: `₹${(car.price / 100000).toFixed(2)}L`
    })),
    ...enquiries.map(enquiry => ({
      type: 'enquiry',
      date: enquiry.createdAt,
      title: `Enquiry from ${enquiry.customerName || 'Customer'}`,
      icon: FiMessageSquare,
      color: 'purple',
      details: enquiry.message?.substring(0, 50) + '...'
    }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div>
      <h3 className="text-lg font-semibold mb-6">Activity Timeline</h3>
      {activities.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No activity yet</p>
      ) : (
        <div className="space-y-4">
          {activities.map((activity, index) => {
            const Icon = activity.icon;
            const colorClasses = {
              green: 'bg-green-100 text-green-600',
              blue: 'bg-blue-100 text-blue-600',
              purple: 'bg-purple-100 text-purple-600'
            };

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start gap-4"
              >
                <div className={`p-2 rounded-lg ${colorClasses[activity.color]}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{activity.title}</p>
                  <p className="text-sm text-gray-500">{activity.details}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(activity.date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Filter Button Component
const FilterButton = ({ active, onClick, label, count }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'bg-orange-600 text-white'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {label} {count !== undefined && `(${count})`}
  </button>
);

export default SellerDetailPage;
